import React, { useState, useEffect } from 'react';

import { LinearProgress, styled } from '@mui/material';
import { linearProgressClasses } from '@mui/material/LinearProgress';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

import Typography from '@mui/material/Typography';
import CardContent from '@mui/material/CardContent';
import '../stylesheet/homePage.css';

import { send_request } from '../../../utils/Request';
import { fetchUsageData } from '../../../utils/UsageAPI';
import FadeIn from 'react-fade-in';
import { Link } from 'react-router-dom';
import { DashboardCard, StyledSkeleton } from './CommonComponents';
import useProjectStore from '../../ProjectCanvas/useProjectStore';

/** Wait for InternalApp to finish billing initialization in useProjectStore */
const waitForBillingInit = () =>
  new Promise(resolve => {
    if (useProjectStore.getState().billingInitialized) return resolve();
    const interval = setInterval(() => {
      if (useProjectStore.getState().billingInitialized) {
        clearInterval(interval);
        resolve();
      }
    }, 50);
  });

const COLORS = {
  primary: '#7B1FA2', // AI purple color used throughout the app
  warning: '#FFA722',
  danger: 'rgba(176, 0, 32, 1)',
  unused: 'rgb(207, 216, 220)',
  text: 'var(--color-text-primary)',
  link: '#2a9af3',
};

const THRESHOLDS = {
  CRITICAL: 90,
  WARNING: 50,
};

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const BorderLinearProgress = styled(LinearProgress, {
  shouldForwardProp: prop => prop !== 'barColor',
})(({ barColor }) => ({
  height: 10,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: 'rgba(0, 0, 0, 0.15)',
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: barColor,
  },
}));

function AiCreditsCard({ tier }) {
  const [usedCredits, setUsedCredits] = useState(null);
  const [totalCredits, setTotalCredits] = useState(null);
  const [renewalDate, setRenewalDate] = useState(null);
  const [percentage, setPercentage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isV2Billing, setIsV2Billing] = useState(null);

  useEffect(() => {
    const initializeCard = async () => {
      // Wait for InternalApp to finish initializing billing info in ProjectStore
      await waitForBillingInit();
      const billingVersion = useProjectStore.getState().isV2Billing ?? false;
      setIsV2Billing(billingVersion);

      if (billingVersion) {
        await fetchV2BillingData();
      } else {
        await fetchLegacyBillingData();
      }
    };

    initializeCard();
  }, []);

  const fetchV2BillingData = async () => {
    try {
      const [usageResponse, clientInfoResponse] = await Promise.all([
        fetchUsageData(),
        send_request('authz-service/organization/get-client-info', '', {}),
      ]);

      if (usageResponse.success && usageResponse.data) {
        const { usedAiCredits: newUsedCredits, totalAiCredits: newTotalCredits } =
          usageResponse.data;

        setUsedCredits(newUsedCredits || 0);
        setTotalCredits(newTotalCredits || 0);

        const calculatedPercentage = calculateUsagePercentage(newUsedCredits, newTotalCredits);
        setPercentage(calculatedPercentage);

        const billingDate = clientInfoResponse?.data?.billingDate;
        const formattedRenewalDate = billingDate ? formatRenewalDate(billingDate) : null;

        setRenewalDate(formattedRenewalDate);
        setLoading(false);
      } else {
        console.error('Failed to fetch usage data:', usageResponse.error);
        await fetchLegacyBillingData();
      }
    } catch (error) {
      console.error('Error fetching V2 billing data:', error);
      await fetchLegacyBillingData();
    }
  };

  const fetchLegacyBillingData = async () => {
    try {
      const [aiQuotaResponse, sessionData] = await Promise.all([
        send_request('authz-service/get_organization_ai_quota_info', '', {}, 'GET'),
        send_request('authz-service/clientstats/get-client-session-data', '', '', 'get'),
      ]);

      if (aiQuotaResponse?.data) {
        // For v1 billing, use the AI quota endpoint which returns usedRequests and totalRequests
        const aiCreditsUsed = aiQuotaResponse.data.usedRequests || 0;
        const aiCreditsTotal = aiQuotaResponse.data.totalRequests || 0;

        setUsedCredits(aiCreditsUsed);
        setTotalCredits(aiCreditsTotal);

        const calculatedPercentage = calculateUsagePercentage(aiCreditsUsed, aiCreditsTotal);
        setPercentage(calculatedPercentage);
      } else {
        // Fallback: set to zero if AI quota endpoint fails
        setUsedCredits(0);
        setTotalCredits(0);
        setPercentage(0);
      }

      // Use billing date from session data
      const billingDate = sessionData.data.lastBillingDate;
      const formattedRenewalDate = formatRenewalDate(billingDate);

      setRenewalDate(formattedRenewalDate);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching legacy billing data:', error);
      setLoading(false);
    }
  };

  const calculateUsagePercentage = (used, total) => {
    if (total === 0) return 0;
    return Math.round((100 * used) / total);
  };

  // BANDAID FIX: Handle past billing dates by advancing them to future
  // TODO: Remove this once webhook issue is fixed on backend
  const formatRenewalDate = dateString => {
    if (!dateString) {
      return null;
    }

    let renewalDate;

    if (dateString && dateString.includes('/')) {
      const [day, month, year] = dateString.split('/');
      renewalDate = new Date(year, month - 1, day);
    } else {
      renewalDate = new Date(dateString);
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    renewalDate.setHours(0, 0, 0, 0);

    // BANDAID: If date is in the past, keep adding months until it's in the future
    let loopCount = 0;
    while (renewalDate < today) {
      loopCount++;
      renewalDate.setMonth(renewalDate.getMonth() + 1);

      // Safety check to prevent infinite loop
      if (loopCount > 12) {
        break;
      }
    }

    const formattedDate = `${renewalDate.getDate()} ${
      MONTH_NAMES[renewalDate.getMonth()]
    } ${renewalDate.getFullYear()}`;

    const timeDiff = renewalDate - today;
    const daysUntilRenewal = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));

    return { formattedDate, daysUntilRenewal };
  };

  const getProgressBarColor = percentage => {
    if (percentage >= THRESHOLDS.CRITICAL) return COLORS.danger;
    if (percentage >= THRESHOLDS.WARNING) return COLORS.warning;
    return COLORS.primary;
  };

  const getProgressBar = (used, total) => {
    const usagePercentage = total === 0 ? 0 : (used / total) * 100;
    const color = getProgressBarColor(usagePercentage);

    return (
      <div className="progress-bar-outer">
        <div className="text-inside-progress-bar" style={{ textAlign: 'left', width: '100%' }} />
        <BorderLinearProgress variant="determinate" value={usagePercentage} barColor={color} />
      </div>
    );
  };

  const creditsWarningComponent = (
    <div className="actionWarningContainer">
      <FadeIn transitionDuration="1000">
        <div className="actionWarningContent">
          <p className="actionWarningText">
            You are about to run out of AI credits for this month. To prevent your AI features from
            being disrupted,{' '}
            <Link to="/organization" style={{ color: COLORS.link }}>
              upgrade your plan
            </Link>
          </p>
        </div>
      </FadeIn>
    </div>
  );

  const aiCreditsComponent = usedCredits !== null && totalCredits !== null && (
    <DashboardCard className="cardStyleActions">
      <CardContent>
        <Typography
          variant="h4"
          component="p"
          className="cardHeader"
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <AutoAwesomeIcon sx={{ mr: 1 }} /> AI Credits used this month
        </Typography>
      </CardContent>

      {percentage >= THRESHOLDS.CRITICAL && creditsWarningComponent}

      <CardContent className="cardTopContent">
        <FadeIn transitionDuration="1000">
          <div className="progress-bar-outer">{getProgressBar(usedCredits, totalCredits)}</div>
          <Typography className="cardDescription">
            <strong style={{ color: COLORS.text }}>{usedCredits.toLocaleString()}</strong> AI
            credits used ({Math.max(0, totalCredits - usedCredits).toLocaleString()} AI credits
            remaining out of{' '}
            <strong style={{ color: COLORS.text }}>{totalCredits.toLocaleString()}</strong>)
          </Typography>
          <Typography className="cardDescription">
            {tier !== 'FREE_TRIAL' && tier !== 'FREE' && renewalDate && (
              <>
                Renews in{' '}
                <strong style={{ color: COLORS.text }}>
                  {renewalDate.daysUntilRenewal}{' '}
                  {renewalDate.daysUntilRenewal === 1 ? 'day' : 'days'}
                </strong>{' '}
                on <strong style={{ color: COLORS.text }}>{renewalDate.formattedDate}</strong>
              </>
            )}
          </Typography>
        </FadeIn>
      </CardContent>
    </DashboardCard>
  );

  // Only show the component if there are AI credits allocated (totalCredits > 0)
  return loading ? (
    <DashboardCard>
      <CardContent>
        <Typography
          variant="h4"
          component="p"
          className="cardHeader"
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <AutoAwesomeIcon sx={{ mr: 1 }} /> AI Credits used this month
        </Typography>
      </CardContent>
      <CardContent className="cardTopContent">
        <StyledSkeleton variant="rectangular" height="10px" margin="16px 0" />
        <StyledSkeleton variant="rectangular" height="20px" width="80%" margin="8px 0" />
        <StyledSkeleton variant="rectangular" height="16px" width="60%" margin="8px 0" />
      </CardContent>
    </DashboardCard>
  ) : (
    totalCredits > 0 && aiCreditsComponent
  );
}

export default AiCreditsCard;
